import {
  getCnpjNumOfDigits,
  addCnpjMask,
  addCpfMask,
  addZipCodeMask,
} from '@repo/ui';
import { GetInternshipLocationByIdUseCaseInterface } from 'domain/usecases';
import * as Yup from 'yup';

export type CreateOrUpdateInternshipLocationFormStructure = ReturnType<
  typeof getCreateOrUpdateInternshipLocationFormInitialValues
>;

export function getCreateOrUpdateInternshipLocationFormInitialValues(
  selectedInternshipLocationData:
    | GetInternshipLocationByIdUseCaseInterface.Result
    | undefined,
) {
  return {
    cnpjOrCpf: selectedInternshipLocationData?.cnpjOrCpf
      ? selectedInternshipLocationData.cnpjOrCpf?.length ===
        getCnpjNumOfDigits()
        ? addCnpjMask(selectedInternshipLocationData.cnpjOrCpf)
        : addCpfMask(selectedInternshipLocationData.cnpjOrCpf)
      : '',
    name: selectedInternshipLocationData?.name || '',
    alternativeName: selectedInternshipLocationData?.alternativeName || '',
    country: selectedInternshipLocationData?.country
      ? {
          value: selectedInternshipLocationData.country.id,
          label: selectedInternshipLocationData.country.name,
        }
      : null,
    nonBrazilianState: selectedInternshipLocationData?.nonBrazilianState || '',
    zipCode:
      (!selectedInternshipLocationData?.country ||
        selectedInternshipLocationData?.country?.id) === 'BR'
        ? addZipCodeMask(selectedInternshipLocationData?.zipCode ?? '')
        : '',
    nonBrazilianZipCode:
      selectedInternshipLocationData?.country &&
      selectedInternshipLocationData?.country.id !== 'BR'
        ? selectedInternshipLocationData?.zipCode ?? ''
        : '',
    street: selectedInternshipLocationData?.street || '',
    addressNumber: selectedInternshipLocationData?.addressNumber || '',
    addressComplement: selectedInternshipLocationData?.addressComplement || '',
    district: selectedInternshipLocationData?.district || '',
    city: selectedInternshipLocationData?.city
      ? {
          value: selectedInternshipLocationData.city.id,
          label: `${selectedInternshipLocationData.city.name} - ${selectedInternshipLocationData.city.federalUnit}`,
        }
      : null,
    nonBrazilianCity: selectedInternshipLocationData?.nonBrazilianCity || '',
    ineCode: selectedInternshipLocationData?.ineCode
      ? Number(selectedInternshipLocationData?.ineCode)
      : undefined,
    observation: selectedInternshipLocationData?.observation || '',
    isRemote: selectedInternshipLocationData?.isRemote ? true : false,
    internshipResponsible: {
      cpf:
        addCpfMask(
          selectedInternshipLocationData?.internshipResponsible?.cpf,
        ) || '',
      name: selectedInternshipLocationData?.internshipResponsible?.name || '',
      role: selectedInternshipLocationData?.internshipResponsible?.role || '',
      phone: selectedInternshipLocationData?.internshipResponsible?.phone
        ? `${'+55' + selectedInternshipLocationData?.internshipResponsible?.phone}`
        : '',
      email: selectedInternshipLocationData?.internshipResponsible?.email || '',
    },
  };
}

export function getCreateOrUpdateInternshipLocationFormValidationSchema() {
  return Yup.object({
    cnpjOrCpf: Yup.string()
      .max(18, 'Máximo de 18 caracteres')
      .cpfOrCnpjType('CNPJ/CPF inválido')
      .notRequired(),
    isRemote: Yup.boolean().notRequired(),
    name: Yup.string()
      .required('Campo é obrigatório')
      .max(255, 'Máximo de 255 caracteres'),
    alternativeName: Yup.string()
      .max(255, 'Máximo de 255 caracteres')
      .notRequired(),
    country: Yup.object()
      .shape({
        label: Yup.string(),
        value: Yup.string(),
      })
      .when('isRemote', {
        is: false,
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    nonBrazilianState: Yup.string()
      .max(100, 'Máximo de 100 caracteres')
      .when(['isRemote', 'country'], {
        is: (isRemote, country) =>
          isRemote === true ? false : country?.value !== 'BR',
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    zipCode: Yup.string()
      .max(9, 'Máximo de 9 caracteres')
      .zipCodeType('CEP inválido')
      .when(['isRemote', 'country'], {
        is: (isRemote, country) =>
          isRemote === true ? false : country?.value === 'BR',
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    nonBrazilianZipCode: Yup.string()
      .max(20, 'Máximo de 20 caracteres')
      .notRequired(),
    city: Yup.object()
      .shape({
        label: Yup.string(),
        value: Yup.number(),
      })
      .when(['isRemote', 'country'], {
        is: (isRemote, country) =>
          isRemote === true
            ? false
            : !country?.value || country?.value === 'BR',
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    nonBrazilianCity: Yup.string()
      .max(100, 'Máximo de 100 caracteres')
      .when(['isRemote', 'country'], {
        is: (isRemote, country) =>
          isRemote === true ? false : country?.value !== 'BR',
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    street: Yup.string()
      .max(255, 'Máximo de 255 caracteres')
      .when('isRemote', {
        is: false,
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    addressNumber: Yup.string()
      .max(10, 'Máximo de 10 caracteres')
      .when('isRemote', {
        is: false,
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    addressComplement: Yup.string()
      .max(100, 'Máximo de 100 caracteres')
      .notRequired(),
    district: Yup.string()
      .max(100, 'Máximo de 100 caracteres')
      .when(['isRemote', 'country'], {
        is: (isRemote, country) =>
          isRemote === true
            ? false
            : !country?.value || country?.value === 'BR',
        then: (schema) => schema.required('Campo é obrigatório'),
        otherwise: (schema) => schema.notRequired(),
      }),
    observation: Yup.string().notRequired(),
    ineCode: Yup.number()
      .lessThan(2147483648, 'Valor máximo de 2.147.483.647')
      .nullable(),
    photo: Yup.mixed()
      .test(
        'fileFormat',
        'Apenas arquivos .png, .jpg e .jpeg são aceitos',
        (value) => {
          if (value && value instanceof File) {
            const supportedFormats = ['image/png', 'image/jpg', 'image/jpeg'];
            return supportedFormats.includes(value.type);
          }
          return true;
        },
      )
      .test(
        'fileMaxSize',
        'Apenas arquivos menores que 5 MB são aceitos',
        (value) => {
          if (value && value instanceof File) {
            const maxSizeInBytes = 5 * 1024 * 1024; // 5 MegaBytes
            return value.size <= maxSizeInBytes;
          }
          return true;
        },
      )
      .notRequired(),
    internshipResponsible: Yup.object({
      cpf: Yup.string()
        .max(14, 'Máximo de 14 caracteres')
        .cpfType('CPF inválido')
        .notRequired(),
      name: Yup.string().max(100, 'Máximo de 100 caracteres').notRequired(),
      role: Yup.string().max(100, 'Máximo de 100 caracteres').notRequired(),
      phone: Yup.string().max(15, 'Máximo de 15 caracteres').notRequired(),
      email: Yup.string()
        .max(100, 'Máximo de 100 caracteres')
        .email('E-mail inválido')
        .notRequired(),
    }),
  });
}
